import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { Box, Chip, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import Button from "../../common/Button";
import { Cancel, Close, DescriptionOutlined, GppMaybeOutlined, History, PictureAsPdfOutlined } from "@mui/icons-material";
import CustomTable from "../../common/CustomTable";
import { Column } from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { selectIsTokenAvailable } from "../../../redux/slice/authSlice";
import type { RootState } from "../../../redux/store";
import CustomIconButton from "../../common/CustomIconButton";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { fetchPatientDocumentsByIds, resetPatientActiveBeneficationInsuranceSummaryData, usePatientReducer } from "../../../redux/slice/patient/patientSlice";
import PDFViewer from "../../common/pdf/PDFViewer";
import { tokens } from "../../../theme";
import { hideElement } from "../../../utils/Utility";
import BenefitVerificationErrorResponse from "./BenefitVerifiactionErrorResponse";
import PatientBenefitInsuranceDetailsPopup from "./PatientBenefitInsuranceDetailsPopup";
import { useModalStyles } from "../../common/useModalStyles";

interface PatientBenefitSummaryHistoryProps {
    open: boolean;
    onClose: () => void;
    patientName: string;
    patientDob:string;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="down" ref={ref} {...props} />;
});

const PatientBenefitSummaryHistoryPopup: React.FC<PatientBenefitSummaryHistoryProps> = ({
    open,
    onClose,
    patientName,
    patientDob
}) => {
    const dispatch = useAppDispatch();
    const canViewEligibilityErrorResponse = useSelector((state: RootState) => selectIsTokenAvailable("canViewEligibilityErrorResponse")(state));
    const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({ message: "", show: false });
    const {  patientBeneficationSummaryData ,patientDocumentInfo } = usePatientReducer();
    const [showPatientBenefitInsuranceSummaryHistoryPopup, setShowPatientBenefitInsuranceSummaryHistoryPopup] = useState(false);
      const [bsvid, setBsvId] = useState<number|null>(null);
        const theme = useTheme();
        const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
        const colors = tokens(theme.palette.mode);
        const {
          tokens: modalTokens,
          paperSx,
          headerSx,
          iconBoxSx,
          bodySx,
          footerSx,
          closeIconSx,
          cancelBtnSx,
        } = useModalStyles();
      const [showPDF, setShowPDF] = useState<{ isShow: boolean, patientName: string, fileName: string }>({ isShow: false, patientName: "", fileName: "" });
    const [showErrorResponsePopup, setShowErrorResponsePopup] = useState<{ isShow: boolean, errorResponse: string }>({ isShow: false, errorResponse: "" });
    const patientBenefitSummaryColumn: Column[] = [
        { id: "requestedDateTimeAndName", label: "Requested Date Time", minWidth: 100 },
        { id: "provider", label: "Provider", minWidth: 100 },
        { id: "insurance", label: "Insurance", minWidth: 100 },
        { id: "policyNo", label: "Policy #", minWidth: 100 },
        { id: "status", label: "Status", minWidth: 100 },
        { id: "pdfButton", label: "Action", minWidth: 100 },
      ];

     const onPdfClick = async (data: any) => {
         //CS-1170
        const actionResult = await dispatch(fetchPatientDocumentsByIds(data.pdfDocId));
        //CS-1170
        if (actionResult.payload) {
          const patientNameFormated =`${patientName} (DOB: ${patientDob}) - Eligibility Check Summary`
          setShowPDF({ isShow: true, patientName:patientNameFormated, fileName:patientNameFormated })
        }
      }
      const showErrorResponse = (data: any) => {
     setShowErrorResponsePopup({ isShow: true, errorResponse: data });
      }

  const patientBenefitActionComponent = (
    row: any): JSX.Element => {
    return (
      <>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          tooltipMessage="PDF"
          tooltipPlacement="top"
          onClick={() => onPdfClick(row)}
          style={{
            display: hideElement(
              !((row.status === "ACTIVE" || row.status === "INACTIVE") && row.pdfDocId !== 0)
            ),
          }}
        >
          <PictureAsPdfOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="error"
          size="small"
          variant="outlined"
          tooltipMessage="Error Response"
          tooltipPlacement="top"
          onClick={() => showErrorResponse(row)}
          style={{
            display: hideElement(
              !(
                row.status !== "ACTIVE" &&
                row.status !== "INACTIVE" &&
                row.pdfDocId === 0
              )
            ),
          }}>
          <GppMaybeOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="warning"
          size="small"
          variant="outlined"
          tooltipMessage="Insurance Eligibility Details"
          tooltipPlacement="top"
          //CS-1170 
          onClick={() => { setShowPatientBenefitInsuranceSummaryHistoryPopup(true); setBsvId(row?.parentBvsId) }}
          //
          style={{
            display: hideElement(
              !((row.status === "ACTIVE" || row.status === "INACTIVE") && row.pdfDocId !== 0)
            ),
          }}
        >
          <DescriptionOutlined fontSize="small" />
        </CustomIconButton>
      </>
    );
  };
    
  const patientBeneficationSummaryRows: any[] = useMemo(() => {
      const rows: any[] = []
    patientBeneficationSummaryData?.map((item: any) => {
      const row = {
        // requestedDateTime: item?.requestedDateTime,
        // CS-1325
        requestedDateTimeAndName: (
          <>
            {item?.requestedDateTime}
            {(
              item?.isAutoVerify || item?.auditUser
            ) && (
              <>
                {" "}
                {/* CS-1325 - Start */}
                <span>
                  {item?.isAutoVerify ? "(AUTO)" : `(${item?.auditUser})`}
                </span>
                {/* CS-1325 - End */}
              </>
            )}
          </>
        ),
        //
        provider: item?.providerName,
        insurance: item.insuranceCompanyName,
        policyNo: item.policyTypeWithNumber,
        status: <Chip
          label={item?.status}
          size="small"
          sx={{
            backgroundColor:
            item.status === "FAILURE" || item.status === "INACTIVE"
                ? "red"
                  : colors.success[300],
            padding: "1px 10px",
            fontWeight: "bold",
            border: "1px solid ",
            borderColor:
            item.status === "FAILURE" || item.status === "INACTIVE"
                ? "red"
                  : colors.success[300],
          }}
        />,
        //CS-1170
        docId: item?.pdfDocId,
        //
        pdfButton: patientBenefitActionComponent(item),
      }
      rows.push(row);
    })
      return rows;
    }, [patientBeneficationSummaryData]);

    return (
        <>
         {showPDF.isShow &&
                <PDFViewer
                  isOpen={showPDF.isShow}
                  onClose={() => { setShowPDF({ isShow: false, patientName: "", fileName: "" }) }}
                  title={showPDF.patientName}
                  fileName={showPDF.fileName}
                  base64String={patientDocumentInfo?.content || ""}
                />
              }
        <PatientBenefitInsuranceDetailsPopup
          open={showPatientBenefitInsuranceSummaryHistoryPopup}
          onClose={() => { setShowPatientBenefitInsuranceSummaryHistoryPopup(false); dispatch(resetPatientActiveBeneficationInsuranceSummaryData()); setBsvId(null); }}
          bvsId={bsvid}
        />
        {showErrorResponsePopup.isShow &&
          <BenefitVerificationErrorResponse
            open={showErrorResponsePopup.isShow}
            onClose={() => { setShowErrorResponsePopup({ isShow: false, errorResponse: "" }) }}
            title="Error Response"
            response={showErrorResponsePopup.errorResponse}
            canViewErrorResponse={canViewEligibilityErrorResponse}
          />}
            <Dialog
                open={open}
                TransitionComponent={Transition}
                keepMounted
                onClose={onClose}
                aria-describedby="alert-dialog-slide-description"
                onClick={(event) => {
                    event.stopPropagation();
                }}
                maxWidth={"lg"}
                fullWidth
                fullScreen={isMobile}
                PaperProps={{ sx: paperSx(isMobile) }}
                sx={{
                  '& .MuiDialog-paper': {
                    maxWidth: { xs: '100vw', md: '1300px' },
                    width: { xs: '100vw', md: 'auto' },
                    m: { xs: 0, md: 2 },
                  }
                }}

            >
                {showOptionPopup.show && (
                    <OptionsPopup
                        open={showOptionPopup.show}
                        onClose={() => setShowOptionsPopup({ message: "", show: false })}
                        variant={showOptionPopup.variant}
                        message={<>{showOptionPopup.message}</>}
                        buttons={showOptionPopup.buttons}
                        title={showOptionPopup.title}
                        obj={showOptionPopup.obj}
                    />
                )}
                <Box sx={headerSx}>
                  <Box sx={iconBoxSx(modalTokens.accent)}>
                    <History sx={{ color: modalTokens.accent, fontSize: 22 }} />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                      <Typography
                        fontWeight={700}
                        sx={{
                          fontSize: "1.1rem",
                          color: modalTokens.textPrimary,
                          lineHeight: 1.3,
                        }}
                      >
                        Patient Eligibility Check History
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: modalTokens.textSecondary,
                        mt: 0.15,
                      }}
                    >
                      Review previous eligibility check requests and related documents
                    </Typography>
                  </Box>

                  <IconButton onClick={onClose} size="small" sx={closeIconSx}>
                    <Close sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    ...bodySx,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    scrollbarGutter: "stable",
                    "&::-webkit-scrollbar": { width: 6, height: 6 },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                      background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                      borderRadius: 4,
                    },
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <Grid container spacing={2} sx={{ margin: 0 }}>
                      <CustomTable
                        columns={patientBenefitSummaryColumn}
                        rows={patientBeneficationSummaryRows || []}
                        // actionComponent={(row: any, rowIndex: number) =>
                        //     patientBenefitActionComponent(row, rowIndex)
                        // }
                        disablePagination={true}
                        borderBox={true}
                        showMenu={true}
                        sx={{ width: { xs: '100%', md: 'auto' }, }}
                      />
                    </Grid>
                  </Box>
                </Box>

                <Box sx={footerSx}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
                onClick={onClose}
                sx={cancelBtnSx}
              >
                Cancel
              </Button>
                  </Box>
                </Box>
            </Dialog>
        </>
    );
};

export default PatientBenefitSummaryHistoryPopup;
